import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  X,
} from "lucide-react";
import { updateDoc, doc } from "firebase/firestore";
import { db } from "../../database/firebase";
import toast from "react-hot-toast";
import { checkAuthState } from "../../redux/slices/authSlice";

const UserOnboarding = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);

  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [showAnalysis, setShowAnalysis] = useState(false);
  const [formData, setFormData] = useState({
    fullName: "",
    age: "",
    gender: "",
    currentWeight: "",
    height: "",
    targetWeight: "",
    waistCircumference: "",
    energyLevel: "5",
    sleepQuality: "",
    stressLevel: "5",
    waterIntake: "",
    primaryGoal: "",
    goalTimeline: "",
    motivationLevel: "5",
    activityLevel: "",
    mealFrequency: "",
    dietType: "",
    supplementUsage: "",
    medicalConditions: "",
    medications: "",
    allergies: "",
    supportSystem: "",
    previousPrograms: "",
    commitmentLevel: "5",
  });

  const questions = [
    {
      id: "fullName",
      question: "What's your name?",
      subtitle: "Your answers will help shape the app around your needs",
      type: "text",
      placeholder: "Enter your full name",
      required: true,
    },
    {
      id: "age",
      question: "How old are you?",
      subtitle: "Your answers will help shape the app around your needs",
      type: "select-card",
      options: [
        { value: "Under 18", label: "Under 18" },
        { value: "18-24", label: "18-24" },
        { value: "25-34", label: "25-34" },
        { value: "35-44", label: "35-44" },
        { value: "45-54", label: "45-54" },
        { value: "55-64", label: "55-64" },
        { value: "65+", label: "65+" },
      ],
      required: true,
    },
    {
      id: "gender",
      question: "What is your gender?",
      subtitle: "For personalized health recommendations",
      type: "select-card",
      options: [
        { value: "Male", label: "👨 Male" },
        { value: "Female", label: "👩 Female" },
        { value: "Other", label: "🧑 Prefer not to say" },
      ],
      required: true,
    },
    {
      id: "currentWeight",
      question: "What's your current weight?",
      subtitle: "Be honest - this is your starting point!",
      type: "text",
      placeholder: "Enter weight in kg",
      required: true,
    },
    {
      id: "height",
      question: "How tall are you?",
      subtitle: "We'll calculate your BMI",
      type: "text",
      placeholder: "Enter height in cm",
      required: true,
    },
    {
      id: "targetWeight",
      question: "What's your target weight?",
      subtitle: "Your wellness goal",
      type: "text",
      placeholder: "Enter target weight in kg",
      required: true,
    },
    {
      id: "primaryGoal",
      question: "What's on your mind?",
      subtitle: "Your answers will help shape the app around your needs",
      type: "select-card",
      options: [
        { value: "Elevate mood", label: "😊 Elevate mood" },
        { value: "Reduce stress and anxiety", label: "😌 Reduce stress and anxiety" },
        { value: "Improve sleep", label: "😴 Improve sleep" },
        { value: "Increase productivity", label: "🚀 Increase productivity" },
        { value: "Something else", label: "❓ Something else" },
      ],
      required: true,
    },
    {
      id: "energyLevel",
      question: "How's your energy level?",
      subtitle: "Rate your daily energy (1-10)",
      type: "slider",
      min: 1,
      max: 10,
      required: true,
    },
    {
      id: "sleepQuality",
      question: "How well do you sleep?",
      subtitle: "Quality matters more than quantity",
      type: "select-card",
      options: [
        { value: "Excellent", label: "😴 Excellent - Sleep like a baby" },
        { value: "Good", label: "😊 Good - Pretty restful" },
        { value: "Fair", label: "😐 Fair - Could be better" },
        { value: "Poor", label: "😩 Poor - Struggle to sleep" },
      ],
      required: true,
    },
    {
      id: "waterIntake",
      question: "Daily water intake?",
      subtitle: "Hydration is key to wellness",
      type: "text",
      placeholder: "Liters per day",
      required: true,
    },
    {
      id: "goalTimeline",
      question: "When do you want to achieve this?",
      subtitle: "Set a realistic timeline",
      type: "select-card",
      options: [
        { value: "1 month", label: "🚀 1 Month - Quick results" },
        { value: "3 months", label: "📅 3 Months - Steady progress" },
        { value: "6 months", label: "🗓️ 6 Months - Sustainable change" },
        { value: "1 year", label: "📋 1 Year - Long-term transformation" },
      ],
      required: true,
    },
    {
      id: "activityLevel",
      question: "How active are you daily?",
      subtitle: "Your typical day-to-day activity",
      type: "select-card",
      options: [
        { value: "Sedentary", label: "💺 Sedentary - Mostly sitting" },
        { value: "Light", label: "🚶 Light - Some walking" },
        { value: "Moderate", label: "🏃 Moderate - Regular movement" },
        { value: "Active", label: "💪 Active - Very physical" },
        { value: "Very Active", label: "🏋️ Very Active - Athlete level" },
      ],
      required: true,
    },
    {
      id: "dietType",
      question: "What's your diet preference?",
      subtitle: "Your eating style",
      type: "select-card",
      options: [
        { value: "Vegetarian", label: "🥗 Vegetarian - No meat" },
        { value: "Non-Vegetarian", label: "🍗 Non-Vegetarian - Includes meat" },
        { value: "Vegan", label: "🌱 Vegan - Plant-based" },
        { value: "Keto", label: "🥑 Keto - Low carb" },
        { value: "Balanced", label: "🍽️ Balanced - Everything in moderation" },
      ],
      required: true,
    },
    {
      id: "medicalConditions",
      question: "Any medical conditions?",
      subtitle: "Help us keep you safe",
      type: "text",
      placeholder: 'List any conditions or type "None"',
      required: true,
    },
    {
      id: "supportSystem",
      question: "Do you have support?",
      subtitle: "Family, friends, or wellness community",
      type: "select-card",
      options: [
        { value: "Strong", label: "👨‍👩‍👧‍👦 Strong Support - Great support system" },
        { value: "Some", label: "👥 Some Support - A few supporters" },
        { value: "Limited", label: "👤 Limited - Mostly solo" },
        { value: "None", label: "🙋 Going Solo - On my own" },
      ],
      required: true,
    },
  ];

  const totalQuestions = questions.length;
  const currentQuestion = questions[currentQuestionIndex];
  const progress = ((currentQuestionIndex + 1) / totalQuestions) * 100;

  const calculateBMI = () => {
    const weight = parseFloat(formData.currentWeight);
    const heightInMeters = parseFloat(formData.height) / 100;
    if (weight && heightInMeters) {
      return (weight / (heightInMeters * heightInMeters)).toFixed(1);
    }
    return null;
  };

  const getBodyType = (bmi) => {
    if (!bmi) return "Unknown";
    if (bmi < 18.5) return "Underweight";
    if (bmi < 25) return "Healthy";
    if (bmi < 30) return "Overweight";
    return "Obese";
  };

  const getHealthStatus = () => {
    const bmi = calculateBMI();
    const bodyType = getBodyType(bmi);
    const weight = parseFloat(formData.currentWeight);
    const target = parseFloat(formData.targetWeight);
    const weightDiff = weight - target;

    return {
      bmi,
      bodyType,
      weightToLose: weightDiff > 0 ? weightDiff.toFixed(1) : 0,
      weightToGain: weightDiff < 0 ? Math.abs(weightDiff).toFixed(1) : 0,
      energyLevel: formData.energyLevel,
      stressLevel: formData.stressLevel,
      motivationLevel: formData.motivationLevel,
    };
  };

  const handleChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      [currentQuestion.id]: value,
    }));
  };

  const nextQuestion = () => {
    if (currentQuestion.required) {
      const value = formData[currentQuestion.id];
      if (!value || value === "") {
        toast.error("This field is required");
        return;
      }
    }

    if (currentQuestionIndex < totalQuestions - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    } else {
      setShowAnalysis(true);
    }
  };

  const prevQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const skipQuestion = () => {
    if (currentQuestionIndex < totalQuestions - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    } else {
      setShowAnalysis(true);
    }
  };

  const handleSubmit = async () => {
    if (!user) {
      toast.error("User not authenticated");
      navigate("/auth");
      return;
    }

    try {
      const userRef = doc(db, "users", user.uid);
      const bmi = calculateBMI();
      const healthStatus = getHealthStatus();

      const wellnessData = {
        wellnessProfile: {
          personalInfo: {
            fullName: formData.fullName,
            age: formData.age,
            gender: formData.gender,
          },
          bodyMetrics: {
            current: {
              weight: parseFloat(formData.currentWeight) || null,
              height: parseFloat(formData.height) || null,
              bmi: parseFloat(bmi) || null,
              waist: parseFloat(formData.waistCircumference) || null,
            },
            target: {
              weight: parseFloat(formData.targetWeight) || null,
              timeline: formData.goalTimeline,
            },
            history: [],
          },
          healthAssessment: {
            energyLevel: parseInt(formData.energyLevel),
            sleepQuality: formData.sleepQuality,
            stressLevel: parseInt(formData.stressLevel),
            waterIntake: parseFloat(formData.waterIntake) || null,
            medicalConditions: formData.medicalConditions,
            medications: formData.medications,
            allergies: formData.allergies,
          },
          lifestyleFactors: {
            activityLevel: formData.activityLevel,
            mealFrequency: formData.mealFrequency,
            dietType: formData.dietType,
            supplementUsage: formData.supplementUsage,
          },
          goals: {
            primary: formData.primaryGoal,
            timeline: formData.goalTimeline,
            motivationLevel: parseInt(formData.motivationLevel),
            commitmentLevel: parseInt(formData.commitmentLevel),
          },
          support: {
            supportSystem: formData.supportSystem,
            previousPrograms: formData.previousPrograms,
          },
          bodyAnalysis: {
            bodyType: healthStatus.bodyType,
            bmi: healthStatus.bmi,
            healthStatus: healthStatus,
            analyzedAt: new Date().toISOString(),
          },
        },
        "personalDetails.isOnboardingComplete": true,
        updatedAt: new Date().toISOString(),
      };

      await updateDoc(userRef, wellnessData);
      await dispatch(checkAuthState());

      toast.success("Welcome to your wellness journey! 🎉");
      navigate("/", { replace: true });
    } catch (error) {
      console.error("Error saving wellness data:", error);
      toast.error("Failed to save wellness data");
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      nextQuestion();
    }
  };

  const renderInput = () => {
    switch (currentQuestion.type) {
      case "select-card":
        return (
          <div className="space-y-3">
            {currentQuestion.options.map((option) => (
              <motion.button
                key={option.value}
                onClick={() => handleChange(option.value)}
                whileTap={{ scale: 0.98 }}
                className={`w-full p-5 rounded-2xl text-left font-medium text-base transition-all ${
                  formData[currentQuestion.id] === option.value
                    ? "bg-[#CDFF00] text-black"
                    : "bg-gray-50 text-black hover:bg-gray-100"
                }`}
              >
                {option.label}
              </motion.button>
            ))}
          </div>
        );

      case "slider":
        const sliderValue = formData[currentQuestion.id] || currentQuestion.min;
        return (
          <div className="space-y-8 py-8">
            <div className="text-center">
              <motion.div
                key={sliderValue}
                initial={{ scale: 0.8 }}
                animate={{ scale: 1 }}
                className="text-7xl font-bold text-black mb-3"
              >
                {sliderValue}
              </motion.div>
              <div className="text-lg text-gray-500">
                out of {currentQuestion.max}
              </div>
            </div>
            <input
              type="range"
              min={currentQuestion.min}
              max={currentQuestion.max}
              value={sliderValue}
              onChange={(e) => handleChange(e.target.value)}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider-thumb"
              style={{
                background: `linear-gradient(to right, #CDFF00 ${
                  ((sliderValue - currentQuestion.min) /
                    (currentQuestion.max - currentQuestion.min)) *
                  100
                }%, #e5e7eb ${
                  ((sliderValue - currentQuestion.min) /
                    (currentQuestion.max - currentQuestion.min)) *
                  100
                }%)`,
              }}
            />
          </div>
        );

      default:
        return (
          <input
            type="text"
            value={formData[currentQuestion.id] || ""}
            onChange={(e) => handleChange(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={currentQuestion.placeholder}
            className="w-full p-5 bg-gray-50 border-2 border-gray-100 rounded-2xl text-lg focus:border-[#CDFF00] focus:ring-4 focus:ring-[#CDFF00]/20 transition-all duration-200 outline-none placeholder-gray-400"
            autoFocus
          />
        );
    }
  };

  // Analysis Screen
  if (showAnalysis) {
    const healthStatus = getHealthStatus();
    const bmi = healthStatus.bmi;
    const bodyType = healthStatus.bodyType;

    return (
      <div className="min-h-screen bg-white flex flex-col">
        {/* Header */}
        <div className="p-6 flex items-center justify-between border-b border-gray-100">
          <button
            onClick={() => setShowAnalysis(false)}
            className="w-10 h-10 rounded-full hover:bg-gray-100 flex items-center justify-center transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-black" />
          </button>
          <h1 className="text-lg font-semibold">Your Analysis</h1>
          <div className="w-10" />
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-8">
          <div className="max-w-md mx-auto space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-4xl font-bold text-black mb-3">
                Analysis Complete! 🎉
              </h2>
              <p className="text-gray-500 text-lg">
                Here's your personalized health overview
              </p>
            </div>

            {/* Health Metrics */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-gray-50 p-6 rounded-3xl">
                <div className="text-sm text-gray-500 font-medium mb-2">BMI</div>
                <div className="text-4xl font-bold text-black">{bmi || "N/A"}</div>
              </div>
              <div className="bg-gray-50 p-6 rounded-3xl">
                <div className="text-sm text-gray-500 font-medium mb-2">
                  Body Type
                </div>
                <div className="text-2xl font-bold text-black">
                  {bodyType}
                </div>
              </div>
              <div className="bg-gray-50 p-6 rounded-3xl">
                <div className="text-sm text-gray-500 font-medium mb-2">
                  Energy
                </div>
                <div className="text-4xl font-bold text-black">
                  {healthStatus.energyLevel}/10
                </div>
              </div>
              <div className="bg-gray-50 p-6 rounded-3xl">
                <div className="text-sm text-gray-500 font-medium mb-2">
                  Goal
                </div>
                <div className="text-xl font-bold text-black">
                  {formData.primaryGoal?.split(" ")[0] || "Set"}
                </div>
              </div>
            </div>

            {/* Weight Goal */}
            {(healthStatus.weightToLose > 0 || healthStatus.weightToGain > 0) && (
              <div className="bg-[#CDFF00] text-black p-6 rounded-3xl">
                <div className="text-lg font-semibold mb-2">Your Goal</div>
                <div className="text-3xl font-bold mb-2">
                  {healthStatus.weightToLose > 0
                    ? `Lose ${healthStatus.weightToLose} kg`
                    : `Gain ${healthStatus.weightToGain} kg`}
                </div>
                <div className="text-sm opacity-80">
                  Timeline: {formData.goalTimeline || "Not set"}
                </div>
              </div>
            )}

            {/* Recommendations */}
            <div className="bg-gray-50 p-6 rounded-3xl">
              <h3 className="font-bold text-black mb-4 text-lg">
                💡 Personalized Recommendations
              </h3>
              <ul className="space-y-3 text-sm text-gray-700">
                <li className="flex items-start gap-2">
                  <span className="text-[#CDFF00] mt-1">✓</span>
                  <span>Start with small, achievable daily goals</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#CDFF00] mt-1">✓</span>
                  <span>Stay hydrated - aim for 2-3 liters daily</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#CDFF00] mt-1">✓</span>
                  <span>Regular exercise 3-5 times per week</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#CDFF00] mt-1">✓</span>
                  <span>Prioritize 7-9 hours of quality sleep</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#CDFF00] mt-1">✓</span>
                  <span>Practice stress management techniques</span>
                </li>
              </ul>
            </div>

            {/* Action Button */}
            <motion.button
              onClick={handleSubmit}
              whileTap={{ scale: 0.98 }}
              className="w-full px-8 py-5 bg-[#CDFF00] text-black rounded-2xl font-bold text-lg shadow-sm hover:shadow-md transition-shadow"
            >
              Start My Wellness Journey
            </motion.button>
          </div>
        </div>

        {/* Bottom Indicator */}
        <div className="h-8 flex items-center justify-center">
          <div className="w-32 h-1 bg-black rounded-full"></div>
        </div>
      </div>
    );
  }

  // Main Onboarding Flow
  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Status Bar */}
      <div className="h-12 flex items-center justify-center px-6">
        <div className="text-sm font-semibold">9:41</div>
      </div>

      {/* Header */}
      <div className="px-6 py-4 flex items-center justify-between">
        <button
          onClick={prevQuestion}
          disabled={currentQuestionIndex === 0}
          className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
            currentQuestionIndex === 0
              ? "text-gray-300 cursor-not-allowed"
              : "text-black hover:bg-gray-100"
          }`}
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        
        <div className="flex-1 mx-4">
          <div className="h-1 bg-gray-100 rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-[#CDFF00] rounded-full"
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.3 }}
            />
          </div>
        </div>

        <button
          onClick={skipQuestion}
          className="text-sm font-medium text-black hover:text-gray-600 transition-colors"
        >
          Skip
        </button>
      </div>

      {/* Question Content */}
      <AnimatePresence mode="wait">
        <motion.div
          key={currentQuestionIndex}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          transition={{ duration: 0.2 }}
          className="flex-1 flex flex-col px-6 pb-6"
        >
          {/* Question */}
          <div className="mb-8">
            <h2 className="text-3xl font-bold text-black mb-3 leading-tight">
              {currentQuestion.question}
            </h2>
            {currentQuestion.subtitle && (
              <p className="text-base text-gray-500 leading-relaxed">
                {currentQuestion.subtitle}
              </p>
            )}
          </div>

          {/* Input Area */}
          <div className="flex-1 overflow-y-auto mb-6">
            {renderInput()}
          </div>

          {/* Continue Button */}
          <motion.button
            onClick={nextQuestion}
            whileTap={{ scale: 0.98 }}
            className="w-full py-5 bg-[#CDFF00] text-black rounded-2xl font-bold text-lg shadow-sm hover:shadow-md transition-shadow"
          >
            Continue
          </motion.button>
        </motion.div>
      </AnimatePresence>

      {/* Bottom Indicator */}
      <div className="h-8 flex items-center justify-center">
        <div className="w-32 h-1 bg-black rounded-full"></div>
      </div>

      <style jsx>{`
        .slider-thumb::-webkit-slider-thumb {
          appearance: none;
          width: 24px;
          height: 24px;
          border-radius: 50%;
          background: #CDFF00;
          cursor: pointer;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
          transition: all 0.2s;
        }
        .slider-thumb::-webkit-slider-thumb:hover {
          transform: scale(1.1);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }
        .slider-thumb::-moz-range-thumb {
          width: 24px;
          height: 24px;
          border-radius: 50%;
          background: #CDFF00;
          cursor: pointer;
          border: none;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
          transition: all 0.2s;
        }
        .slider-thumb::-moz-range-thumb:hover {
          transform: scale(1.1);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }
      `}</style>
    </div>
  );
};

export default UserOnboarding;
